/**
 * ROI calculation debug logging.
 * Enable: REACT_APP_ROI_CALC_DEBUG=true  OR  localStorage.setItem('roiCalcDebug', '1')
 * Disable: localStorage.removeItem('roiCalcDebug')
 */

import { resolveRoiVariableEntry } from "./roiVariableResolve";

const STYLE = "color:#0d6efd;font-weight:600";
const WARN_STYLE = "color:#d97706;font-weight:600";
const OK_STYLE = "color:#16a34a;font-weight:600";
const FAIL_STYLE = "color:#dc2626;font-weight:600";

export function isRoiCalcDebugEnabled() {
  try {
    if (typeof window !== "undefined" && window.localStorage?.getItem("roiCalcDebug") === "1") {
      return true;
    }
  } catch (_) {
    /* ignore */
  }
  return process.env.REACT_APP_ROI_CALC_DEBUG === "true";
}

export function roiCalcLog(...args) {
  if (!isRoiCalcDebugEnabled()) {
    return;
  }
  console.log("%c[ROI-Calc]", STYLE, ...args);
}

export function roiCalcWarn(...args) {
  if (!isRoiCalcDebugEnabled()) {
    return;
  }
  console.warn("%c[ROI-Calc]", WARN_STYLE, ...args);
}

export function roiCalcGroup(label, fn) {
  if (!isRoiCalcDebugEnabled()) {
    return fn();
  }
  console.groupCollapsed(`%c[ROI-Calc] ${label}`, STYLE);
  try {
    return fn();
  } finally {
    console.groupEnd();
  }
}

export function describeVariable(roiArr, variableId, caches = {}) {
  if (!roiArr || !variableId) {
    return { variableId, found: false };
  }
  const resolved = resolveRoiVariableEntry(roiArr, variableId, caches);
  const entry = resolved.entry;
  if (!entry) {
    return { variableId, found: false };
  }
  return {
    variableId,
    resolvedKey: resolved.resolvedKey,
    found: true,
    name: entry.text || entry.label || "",
    type: entry.type || entry.variable_data_type || "",
    value: resolved.value ?? entry.value,
    selectedIndex: entry.selectedIndex,
    currency: entry.currency,
    componentUuid: entry.componentUuid,
    fieldId: entry.fieldId,
  };
}

/** Snapshot Radio / NestedRadioGroup values for quick inspection. */
export function logRadioSnapshot(roiArr, label = "Radio snapshot") {
  if (!isRoiCalcDebugEnabled() || !roiArr) {
    return;
  }
  const radios = Object.keys(roiArr)
    .filter((key) => {
      const t = roiArr[key]?.type;
      return t === "Radio" || t === "NestedRadioGroup";
    })
    .map((key) => ({
      key,
      name: roiArr[key]?.text || "",
      value: roiArr[key]?.value,
      selectedIndex: roiArr[key]?.selectedIndex,
      type: roiArr[key]?.type,
    }));

  const nestedChildren = Object.keys(roiArr)
    .filter((key) => key.includes("__child"))
    .map((key) => ({
      key,
      name: roiArr[key]?.text || "",
      value: roiArr[key]?.value,
      selectedIndex: roiArr[key]?.selectedIndex,
    }));

  roiCalcGroup(label, () => {
    console.table(radios.length ? radios : [{ note: "No Radio variables in roiActionArr" }]);
    if (nestedChildren.length) {
      console.log("Nested radio child keys:");
      console.table(nestedChildren);
    }
  });
}

export function logConditionResult({
  ruleId,
  conditionIndex,
  condition,
  variableDesc,
  compareValue,
  operator,
  result,
}) {
  if (!isRoiCalcDebugEnabled()) {
    return;
  }
  const prefix = ruleId ? `Rule ${ruleId}` : "Condition";
  const idx = conditionIndex !== undefined ? ` #${conditionIndex}` : "";
  const style = result ? OK_STYLE : FAIL_STYLE;
  console.log(
    `%c[ROI-Calc] ${prefix}${idx}`,
    style,
    {
      operator,
      variable: variableDesc,
      compareTo: compareValue,
      valueType: condition?.value_type,
      dataType: condition?.variable_data_type,
      result: result ? "PASS" : "FAIL",
    }
  );
}
